import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, getQueryFn, queryClient } from "@/lib/queryClient";
import { PageHeader } from "@/components/shared/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { formatDateTime } from "@/lib/format";

type PartnerUser = {
  id: string;
  email: string;
  status: "active" | "inactive" | "pending";
  invitedAt: string | null;
  acceptedAt: string | null;
};

type Partner = {
  id: string;
  name: string;
  status: "active" | "disabled";
  folderId: string;
  folderName: string;
  createdAt: string;
  users: PartnerUser[];
};

export default function ClientPartnersPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  const { data: partners = [], isLoading } = useQuery<Partner[]>({
    queryKey: ["/api/portal/partners"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/portal/partners", {
        name,
        email,
      });
      return res.json();
    },
    onSuccess: async () => {
      setName("");
      setEmail("");
      await queryClient.invalidateQueries({ queryKey: ["/api/portal/partners"] });
      await queryClient.invalidateQueries({
        queryKey: ["/api/portal/files/folders"],
      });
      toast({ title: "Supplier invited", description: "They will receive an email to set a password." });
    },
    onError: (error: Error) => {
      toast({
        title: "Could not add supplier",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const disableMutation = useMutation({
    mutationFn: async (partnerId: string) => {
      await apiRequest("POST", `/api/portal/partners/${partnerId}/disable`);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/portal/partners"] });
      toast({ title: "Supplier disabled" });
    },
    onError: (error: Error) => {
      toast({
        title: "Could not disable supplier",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const resendMutation = useMutation({
    mutationFn: async (partnerId: string) => {
      await apiRequest(
        "POST",
        `/api/portal/partners/${partnerId}/resend-invite`,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/portal/partners"] });
      toast({ title: "Invite sent" });
    },
    onError: (error: Error) => {
      toast({
        title: "Could not resend invite",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Suppliers"
        description="Invite a supplier to upload into their own folder. They cannot see your other files."
      />

      <Card>
        <CardContent className="grid gap-4 pt-6 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
          <div className="space-y-2">
            <Label htmlFor="partner-name">Partner name</Label>
            <Input
              id="partner-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Acme Print"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="partner-email">Email</Label>
            <Input
              id="partner-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="uploads@example.com"
            />
          </div>
          <Button
            type="button"
            disabled={!name.trim() || !email.trim() || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            {createMutation.isPending ? "Inviting…" : "Invite"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : !partners.length ? (
            <p className="text-sm text-muted-foreground">
              No suppliers yet. Add a partner name and email to create their folder.
            </p>
          ) : (
            <div className="divide-y rounded-md border">
              {partners.map((partner) => {
                const login = partner.users[0];
                return (
                  <div
                    key={partner.id}
                    className="flex flex-col gap-3 px-3 py-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-medium">{partner.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {login?.email ?? "No login"}
                        {" · "}
                        {partner.status === "disabled"
                          ? "Disabled"
                          : login?.status === "pending"
                            ? "Invite sent"
                            : "Active"}
                        {" · Folder "}
                        {partner.folderName}
                        {login?.invitedAt
                          ? ` · Invited ${formatDateTime(login.invitedAt)}`
                          : ""}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={resendMutation.isPending}
                        onClick={() => resendMutation.mutate(partner.id)}
                      >
                        {partner.status === "disabled" ? "Re-invite" : "Resend invite"}
                      </Button>
                      {partner.status !== "disabled" && (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          disabled={disableMutation.isPending}
                          onClick={() => disableMutation.mutate(partner.id)}
                        >
                          Disable
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
